import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { ThemeProvider } from '@ucl/fpds/themeProvider';
import { Button } from '@ucl/fpds/button';
import { Snackbar } from '@ucl/fpds/snackbar';
import { Drawer } from '@ucl/fpds/drawer';
import { Pagination } from '@ucl/fpds/pagination';
import { Icon } from '@ucl/fpds/icons';
import '../../styles.css';
export const SERVICE_HISTORY_QUERY = `
  query ServiceHistoryRecords($vin: String, $repairOrderId: String, $ffmCompanyId: String, $limit: Int, $offset: Int) {
    getServiceHistory(vin: $vin, repairOrderId: $repairOrderId, ffmCompanyId: $ffmCompanyId, limit: $limit, offset: $offset) {
      totalCount
      hasNextPage
      items {
        vin
        repairOrderId
        dataSource
        url
        repairOrderCloseDate
        totalAmount
        odometerReading
        engineHours
        serviceDescription
        ffmCompanyId
        partsTotalAmount
        laborTotalAmount
        miscellaneousTotalAmount
        taxTotalAmount
      }
    }
  }
`;
const DEFAULT_GRAPHQL_ENDPOINT = '/fis/sm/graphql';
function formatCellValue(value, fallback = '-') {
    if (value === null || value === undefined) {
        return fallback;
    }
    const asString = String(value).trim();
    return asString.length > 0 ? asString : fallback;
}
function formatDataSourceLabel(value) {
    const source = formatCellValue(value).toLowerCase();
    if (source === 'dcds')
        return 'Ford Dealership';
    if (source === 'fleetio')
        return 'Ford Fleet Management';
    if (source === 'telematics')
        return 'Manually Added';
    return formatCellValue(value);
}
export function mapGraphqlServiceHistoryResponse(payload) {
    const serviceHistoryData = payload.data?.getServiceHistory;
    const list = serviceHistoryData?.items ?? payload.data?.serviceHistory ?? payload.data?.serviceHistoryRecords;
    if (!Array.isArray(list)) {
        return [];
    }
    return list.map((item) => {
        const row = (item ?? {});
        const yearPart = row.vehicleYear ? String(row.vehicleYear).trim() : '';
        const makePart = row.vehicleMake ? String(row.vehicleMake).trim() : '';
        const modelPart = row.vehicleModel ? String(row.vehicleModel).trim() : '';
        const vehicleNameParts = [yearPart, makePart, modelPart].filter(Boolean);
        return {
            serviceHistoryNo: formatCellValue(row.repairOrderId).replace(/^\[|\]$/g, '').trim(),
            vin: formatCellValue(row.vin),
            vehicleName: vehicleNameParts.length > 0 ? vehicleNameParts.join(' ') : formatCellValue(row.vin),
            dataSource: formatDataSourceLabel(row.dataSource),
            url: formatCellValue(row.url),
            date: formatCellValue(row.repairOrderCloseDate),
            odometerMiles: formatCellValue(row.odometerReading),
            engineHours: formatCellValue(row.engineHours),
            serviceCenter: formatCellValue(row.ffmCompanyId, '-'),
            issuesAddressed: formatCellValue(row.serviceDescription),
            serviceCategory: formatCellValue(row.serviceDescription),
            laborCost: formatCellValue(row.laborTotalAmount),
            partsCost: formatCellValue(row.partsTotalAmount),
            totalCost: formatCellValue(row.totalAmount),
        };
    });
}
export function buildServiceHistoryCsv(rows) {
    const headers = [
        'Service History No',
        'VIN',
        'Date',
        'Odometer(mi)',
        'Engine Hours',
        'Service Center',
        'Issues Addressed',
        'Service Categories',
        'Total Cost',
    ];
    const escape = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const contentRows = rows.map((row) => [
        row.serviceHistoryNo,
        row.vin,
        row.date,
        row.odometerMiles,
        row.engineHours,
        row.serviceCenter,
        row.issuesAddressed,
        row.serviceCategory,
        row.totalCost,
    ]
        .map(escape)
        .join(','));
    return [headers.join(','), ...contentRows].join('\n');
}
function getGraphqlEndpoint() {
    return process.env.BFF_GRAPHQL_ENDPOINT || DEFAULT_GRAPHQL_ENDPOINT;
}
async function fetchServiceHistoryRows(params) {
    const variables = {
        limit: params?.limit ?? 100,
        offset: params?.offset ?? 0,
    };
    if (params?.vin)
        variables.vin = params.vin;
    if (params?.repairOrderId)
        variables.repairOrderId = params.repairOrderId;
    if (params?.ffmCompanyId)
        variables.ffmCompanyId = params.ffmCompanyId;
    const response = await fetch(getGraphqlEndpoint(), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ query: SERVICE_HISTORY_QUERY, variables }),
    });
    if (!response.ok) {
        throw new Error(`Failed to fetch service history: ${response.status}`);
    }
    const payload = (await response.json());
    if (Array.isArray(payload.errors) && payload.errors.length > 0) {
        const firstError = payload.errors[0]?.message || 'GraphQL query failed';
        throw new Error(firstError);
    }
    return mapGraphqlServiceHistoryResponse(payload);
}
function getSortValue(row, column) {
    switch (column) {
        case 'odometerMiles': {
            const value = Number(row.odometerMiles);
            return Number.isFinite(value) ? value : Number.NEGATIVE_INFINITY;
        }
        case 'engineHours': {
            const value = Number(row.engineHours);
            return Number.isFinite(value) ? value : Number.NEGATIVE_INFINITY;
        }
        case 'totalCost': {
            const value = Number(row.totalCost);
            return Number.isFinite(value) ? value : Number.NEGATIVE_INFINITY;
        }
        default:
            return row[column].toLowerCase();
    }
}
const COLUMNS = [
    { key: 'serviceHistoryNo', label: 'Service History No', sortable: true },
    { key: 'date', label: 'Date', sortable: true },
    { key: 'odometerMiles', label: 'Odometer(mi)', sortable: true },
    { key: 'engineHours', label: 'Engine Hours', sortable: true },
    { key: 'serviceCenter', label: 'Service Center', sortable: true },
    { key: 'issuesAddressed', label: 'Issues Addressed', sortable: true },
    { key: 'serviceCategory', label: 'Service Categories', sortable: false },
    { key: 'totalCost', label: 'Total Cost', sortable: false },
];
function ServiceHistoryVehicleExpandedView({ vin, repairOrderId, ffmCompanyId, limit, offset }) {
    const [rows, setRows] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [sortConfig, setSortConfig] = useState({ column: 'date', direction: 'desc' });
    const [drawerRow, setDrawerRow] = useState(null);
    const [downloadToast, setDownloadToast] = useState(null);
    const toastTimeoutRef = useRef(null);
    const showDownloadToast = (message, tone) => {
        setDownloadToast({ message, tone });
        if (toastTimeoutRef.current !== null) {
            window.clearTimeout(toastTimeoutRef.current);
        }
        toastTimeoutRef.current = window.setTimeout(() => {
            setDownloadToast(null);
            toastTimeoutRef.current = null;
        }, 3500);
    };
    useEffect(() => {
        let isMounted = true;
        const fetchServiceHistory = async () => {
            setIsLoading(true);
            setErrorMessage('');
            try {
                const fetchedRows = await fetchServiceHistoryRows({ vin, repairOrderId, ffmCompanyId, limit, offset });
                if (!isMounted) {
                    return;
                }
                setRows(fetchedRows);
                setCurrentPage(1);
            }
            catch {
                if (!isMounted) {
                    return;
                }
                setRows([]);
                setErrorMessage('Unable to load service history right now.');
            }
            finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };
        void fetchServiceHistory();
        return () => {
            isMounted = false;
        };
    }, [vin, repairOrderId, ffmCompanyId, limit, offset]);
    useEffect(() => {
        return () => {
            if (toastTimeoutRef.current !== null) {
                window.clearTimeout(toastTimeoutRef.current);
            }
        };
    }, []);
    const filteredRows = rows;
    const sortedRows = useMemo(() => {
        if (!sortConfig) {
            return filteredRows;
        }
        const { column, direction } = sortConfig;
        return [...filteredRows].sort((rowA, rowB) => {
            const valueA = getSortValue(rowA, column);
            const valueB = getSortValue(rowB, column);
            if (valueA < valueB) {
                return direction === 'asc' ? -1 : 1;
            }
            if (valueA > valueB) {
                return direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }, [filteredRows, sortConfig]);
    const paginatedRows = useMemo(() => {
        const startIndex = (currentPage - 1) * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        return sortedRows.slice(startIndex, endIndex);
    }, [sortedRows, currentPage, rowsPerPage]);
    const handleDownloadCsv = async () => {
        try {
            const latestRows = await fetchServiceHistoryRows({ vin, repairOrderId, ffmCompanyId, limit, offset });
            const csvContent = buildServiceHistoryCsv(latestRows);
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const downloadUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', 'service-history.csv');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(downloadUrl);
            showDownloadToast('Service History downloaded successfully', 'success');
        }
        catch {
            showDownloadToast("Couldn't download Service History", 'error');
        }
    };
    return (_jsxs(ThemeProvider, { productTeamName: "rpst-sm-unified-service-ui", children: [_jsxs("section", { "aria-label": "service history vehicle expanded view", className: "shvev-root", children: [_jsx("div", { className: "shvev-controls", children: _jsx("div", { className: "shvev-controls-top shvev-controls-top--left", children: _jsxs(Button, { variant: "secondary", className: "shvev-download-btn", onClick: handleDownloadCsv, children: [_jsx(Icon, { icon: "download", size: "s" }), "Download Service History"] }) }) }), isLoading && _jsx("p", { role: "status", children: "Loading service history..." }), !isLoading && errorMessage && _jsx("p", { role: "alert", children: errorMessage }), !isLoading && !errorMessage && (_jsxs("div", { className: "shvev-table-shell", children: [_jsxs("table", { className: "shvev-table", children: [_jsx("thead", { children: _jsx("tr", { children: COLUMNS.map((column) => {
                                                const isActive = sortConfig?.column === column.key;
                                                const isSortable = column.sortable;
                                                return (_jsx("th", { scope: "col", "aria-sort": isSortable && isActive ? (sortConfig?.direction === 'asc' ? 'ascending' : 'descending') : undefined, children: isSortable ? (_jsxs("button", { type: "button", className: "shvev-th-button", onClick: () => {
                                                            const nextDirection = isActive && sortConfig?.direction === 'asc' ? 'desc' : 'asc';
                                                            setSortConfig({ column: column.key, direction: nextDirection });
                                                            setCurrentPage(1);
                                                        }, "aria-label": `Sort by ${column.label}${isActive ? ` (${sortConfig?.direction === 'asc' ? 'ascending' : 'descending'})` : ''}`, children: [column.label, isActive ? (_jsx("span", { className: "shvev-sort-icon--active", "aria-hidden": "true", children: sortConfig?.direction === 'asc' ? '▲' : '▼' })) : (_jsxs("span", { className: "shvev-sort-icon", "aria-hidden": "true", children: [_jsx("span", { className: "shvev-sort-tri", children: "\u25B2" }), _jsx("span", { className: "shvev-sort-tri", children: "\u25BC" })] }))] })) : (column.label) }, column.key));
                                            }) }) }), _jsx("tbody", { children: paginatedRows.map((row) => (_jsxs("tr", { children: [_jsxs("td", { children: [_jsxs(Button, { variant: "tertiary", className: "shvev-service-link-btn", onClick: () => setDrawerRow(row), "aria-label": `View details for [${row.serviceHistoryNo}]`, children: ["[", row.serviceHistoryNo, "]"] }), _jsx("span", { className: "shvev-source-subtext", children: row.dataSource })] }), _jsx("td", { children: row.date }), _jsx("td", { children: row.odometerMiles }), _jsx("td", { children: row.engineHours }), _jsx("td", { children: row.serviceCenter }), _jsx("td", { children: row.issuesAddressed }), _jsx("td", { children: row.serviceCategory }), _jsx("td", { children: row.totalCost })] }, row.serviceHistoryNo))) })] }), sortedRows.length === 0 && _jsx("p", { className: "shvev-empty", children: "No service history records found." }), downloadToast && (_jsx("div", { className: "shvev-snackbar-wrapper", children: _jsx(Snackbar, { type: downloadToast.tone === 'error' ? 'error' : 'primary', message: downloadToast.tone === 'error' ? "Couldn't download Service History." : 'Service History Downloaded Successfully.', dismissible: true, onClose: () => {
                                        setDownloadToast(null);
                                        if (toastTimeoutRef.current !== null) {
                                            window.clearTimeout(toastTimeoutRef.current);
                                            toastTimeoutRef.current = null;
                                        }
                                    } }) })), sortedRows.length > 0 && (_jsxs("div", { className: "shvev-pagination", children: [_jsxs("div", { className: "shvev-pagination-left", children: [_jsx(Pagination, { totalItems: sortedRows.length, itemsPerPage: rowsPerPage, page: currentPage, showPerPageOption: true, showTotalItems: false, hidePageListButtons: true, contentAlignment: "left", perPageOptions: [10, 20, 30, 40, 50, 100], initialItemsPerPage: rowsPerPage, onItemsPerPageChange: (val) => {
                                                    setRowsPerPage(val);
                                                    setCurrentPage(1);
                                                } }), _jsxs("span", { className: "shvev-pagination-total", children: [sortedRows.length, " Total"] })] }), _jsx(Pagination, { totalItems: sortedRows.length, itemsPerPage: rowsPerPage, page: currentPage, onPageChange: (page) => setCurrentPage(page), showPerPageOption: false, showTotalItems: false, contentAlignment: "right" })] }))] }))] }), _jsxs(Drawer, { isOpen: drawerRow !== null, onClose: () => setDrawerRow(null), placement: "right", size: "medium", children: [_jsx(Drawer.Header, { titleProp: { title: drawerRow?.serviceHistoryNo ?? '', headingLevel: 'h2' }, onClose: () => setDrawerRow(null) }), _jsx(Drawer.Content, { children: drawerRow !== null && (_jsxs("div", { className: "shvev-drawer-body", children: [_jsxs("div", { className: "shvev-drawer-row", children: [_jsx("span", { className: "shvev-drawer-label", children: "Data Source" }), _jsx("span", { className: "shvev-drawer-value", children: drawerRow.dataSource })] }), _jsx("div", { className: "shvev-drawer-divider" }), _jsxs("div", { className: "shvev-drawer-row", children: [_jsx("span", { className: "shvev-drawer-label", children: "Date" }), _jsx("span", { className: "shvev-drawer-value", children: drawerRow.date })] }), _jsx("div", { className: "shvev-drawer-divider" }), _jsxs("div", { className: "shvev-drawer-row", children: [_jsx("span", { className: "shvev-drawer-label", children: "Odometer" }), _jsxs("span", { className: "shvev-drawer-value", children: [drawerRow.odometerMiles, " mi"] })] }), _jsx("div", { className: "shvev-drawer-divider" }), _jsxs("div", { className: "shvev-drawer-row", children: [_jsx("span", { className: "shvev-drawer-label", children: "Engine Hours" }), _jsx("span", { className: "shvev-drawer-value", children: drawerRow.engineHours })] }), _jsx("div", { className: "shvev-drawer-divider" }), _jsxs("div", { className: "shvev-drawer-row", children: [_jsx("span", { className: "shvev-drawer-label", children: "Service Center" }), _jsx("span", { className: "shvev-drawer-value", children: drawerRow.serviceCenter })] }), _jsx("div", { className: "shvev-drawer-divider" }), _jsxs("div", { className: "shvev-drawer-row", children: [_jsx("span", { className: "shvev-drawer-label", children: "Service Categories" }), _jsx("span", { className: "shvev-drawer-value", children: drawerRow.serviceCategory })] }), _jsx("div", { className: "shvev-drawer-divider" }), _jsxs("div", { className: "shvev-drawer-row", children: [_jsx("span", { className: "shvev-drawer-label", children: "Issues Addressed" }), _jsx("span", { className: "shvev-drawer-value", children: drawerRow.issuesAddressed })] }), _jsx("div", { className: "shvev-drawer-divider" }), _jsxs("div", { className: "shvev-drawer-row", children: [_jsx("span", { className: "shvev-drawer-label", children: "Labor" }), _jsxs("span", { className: "shvev-drawer-value", children: ["Cost: ", drawerRow.laborCost, " USD"] })] }), _jsx("div", { className: "shvev-drawer-divider" }), _jsxs("div", { className: "shvev-drawer-row", children: [_jsx("span", { className: "shvev-drawer-label", children: "Parts" }), _jsxs("span", { className: "shvev-drawer-value", children: ["Cost: ", drawerRow.partsCost, " USD"] })] }), _jsx("div", { className: "shvev-drawer-divider" }), _jsxs("div", { className: "shvev-drawer-row", children: [_jsx("span", { className: "shvev-drawer-label", children: "Total Cost" }), _jsxs("span", { className: "shvev-drawer-value", children: [drawerRow.totalCost, " USD"] })] })] })) }), _jsx(Drawer.Footer, { children: _jsx(Button, { variant: "secondary", onClick: () => setDrawerRow(null), children: "Close" }) })] })] }));
}
export default ServiceHistoryVehicleExpandedView;
